import { type InjectedFormikProps } from "formik";
import { useCallback } from "react";

import { BdsButton, BdsIconGearSix, BdsLayout, BdsSection } from "@buildertrend/components";

import { BTLoginTypes, InternalUserActivationStatus } from "types/enum";

import { BTCard } from "commonComponents/btWrappers/BTCard/BTCard";
import { BTFormItemAutomatic, type ILabelSchema } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTIconArrowRightOutlined } from "commonComponents/btWrappers/BTIcon";
import { BTInput } from "commonComponents/btWrappers/BTInput/BTInput";
import { BTLink } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { CellPhoneInput } from "commonComponents/entity/cellPhone/CellPhoneInput/CellPhoneInput";
import { SmsOptInStatus } from "commonComponents/entity/cellPhone/CellPhoneInput/CellPhoneInput.types";
import { CellPhoneOptInStatusTag } from "commonComponents/entity/cellPhone/CellPhoneInput/CellPhoneOptinStatusTag";
import { EmailFormEntry } from "commonComponents/entity/emailFormEntry/EmailFormEntry";
import { RoleSelect } from "commonComponents/entity/roles/RoleSelect/RoleSelect";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";
import { PhoneActionSheet } from "commonComponents/utilities/PhoneActionSheet/PhoneActionSheet";
import { ReminderPopover } from "commonComponents/utilities/ReminderPopover/ReminderPopover";
import { RequiredFieldIndicator } from "commonComponents/utilities/RequiredFieldIndicator/RequiredFieldIndicator";
import { ValueDisplay } from "commonComponents/utilities/ValueDisplay/ValueDisplay";

import {
    type IInternalUserFormValues,
    type InternalUserEntity,
    InternalUserTabKeys,
} from "entity/internalUser/InternalUserDetails/InternalUser.api.types";

import "./Overview.less";

interface IInternalUserOverviewProps {
    internalUserDetails: InternalUserEntity;
    canEdit: boolean;
    onChangeActiveKey: (value: InternalUserTabKeys) => void;
    manageRolesLink: string;
    onInviteClick: () => void;
    onRoleChange: (field: string, value: any) => void;
    isEmailRequired: boolean;
    inEditMode: boolean;
}

export function InternalUserOverview({
    canEdit,
    values,
    onRoleChange,
    internalUserDetails,
    setFieldValue,
    setFieldTouched,
    initialValues,
    onChangeActiveKey,
    manageRolesLink,
    onInviteClick,
    inEditMode,
    ...props
}: InjectedFormikProps<IInternalUserOverviewProps, IInternalUserFormValues>) {
    const handleLinkClick = useCallback(
        (tab: InternalUserTabKeys) => {
            onChangeActiveKey(tab);
        },
        [onChangeActiveKey],
    );

    const primaryEmailInputLabel = useCallback(
        (schema: ILabelSchema) => (
            <>
                Primary Email {schema.isRequiredFieldIndicatorVisible && <RequiredFieldIndicator />}
            </>
        ),
        [],
    );

    const { activationStatus } = internalUserDetails.internalUser;
    const roleTreeData = values.roleTreeData;
    const selectedRole = roleTreeData.find((e) => parseInt(e.id) === values.role)!;
    const isFullAdmin =
        internalUserDetails.canView &&
        internalUserDetails.canEdit &&
        internalUserDetails.canAdd &&
        internalUserDetails.canDelete;
    const canView = internalUserDetails.canView;
    const isLinkedToIdp =
        internalUserDetails.internalUser.id !== 0 &&
        activationStatus === InternalUserActivationStatus.ActiveInviteAccepted;
    const isReadOnly = !canView || !canEdit || !inEditMode;

    return (
        <PageSection
            title="Overview"
            className="OverviewSection"
            extra={
                <div className="flex flex-row flex align-items-center grow">
                    <div className="flex flex-col">
                        <ValueDisplay
                            value={
                                values.sendInvitation
                                    ? "Invite will be sent on save."
                                    : internalUserDetails.internalUser.inviteStatus
                            }
                        />
                    </div>
                    <div className="flex flex-col">
                        {canEdit &&
                            activationStatus ===
                                InternalUserActivationStatus.ActiveInvitePending && (
                                <ReminderPopover
                                    loginType={BTLoginTypes.BUILDER}
                                    placement={inEditMode ? "top" : "topLeft"}
                                />
                            )}
                    </div>
                    <div className="flex flex-col">
                        {!values.sendInvitation &&
                            internalUserDetails.internalUser.inviteButtonText &&
                            canEdit &&
                            inEditMode && (
                                <BdsButton
                                    displayType="secondary"
                                    testid="btn-InviteUser"
                                    className="margin-left-xs"
                                    onClick={onInviteClick}
                                    disabled={!values.loginEnabled}
                                    text={internalUserDetails.internalUser.inviteButtonText}
                                />
                            )}
                        {values.sendInvitation && canEdit && (
                            <BdsButton
                                displayType="secondary"
                                testid="btn-InviteUser"
                                className="margin-left-xs"
                                onClick={onInviteClick}
                                disabled={!values.loginEnabled}
                                text="Cancel Invite"
                            />
                        )}
                    </div>
                </div>
            }
        >
            <BdsLayout displayType="1and1" id="nameSection" testid="nameSection">
                <BdsSection>
                    <BTFormItemAutomatic<IInternalUserFormValues> id="firstName" useBds>
                        <BTInput
                            id="firstName"
                            data-testid="firstName"
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                            readOnly={isReadOnly}
                            value={values.firstName}
                        />
                    </BTFormItemAutomatic>
                </BdsSection>
                <BdsSection>
                    <BTFormItemAutomatic<IInternalUserFormValues> id="lastName" useBds>
                        <BTInput
                            id="lastName"
                            data-testid="lastName"
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                            readOnly={isReadOnly}
                            value={values.lastName}
                        />
                    </BTFormItemAutomatic>
                </BdsSection>
            </BdsLayout>
            <EmailFormEntry
                {...props}
                useBds
                values={values}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                initialValues={initialValues}
                inputFieldReadOnly={isReadOnly || isLinkedToIdp}
                showPrimaryEmailField={true}
                maxCount={6}
                primaryEmailInputLabel={primaryEmailInputLabel}
                readOnly={isReadOnly}
                innerGutterWidth={8}
            />
            <BdsLayout displayType="1and1" id="phoneSection" testid="phoneSection">
                <BdsSection>
                    <BTFormItemAutomatic<IInternalUserFormValues> id="phone" useBds>
                        {!isReadOnly || !values.phone ? (
                            <BTInput
                                id="phone"
                                data-testid="phone"
                                onChange={setFieldValue}
                                onBlur={setFieldTouched}
                                readOnly={isReadOnly}
                                value={values.phone}
                            />
                        ) : (
                            <PhoneActionSheet
                                value={values.phone}
                                testIdBase="phoneActions"
                                defaultCountryCode={internalUserDetails.internalUser.countryCode}
                            />
                        )}
                    </BTFormItemAutomatic>
                </BdsSection>
                <BdsSection>
                    {!isReadOnly || !values.phoneCell ? (
                        <CellPhoneInput
                            defaultCountryCode={internalUserDetails.internalUser.countryCode}
                            values={values}
                            initialValues={initialValues}
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                            onPreferencesClicked={() =>
                                handleLinkClick(InternalUserTabKeys.Notifications)
                            }
                            readOnly={!inEditMode}
                            useBds
                        />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-xs">
                            <BTFormItemAutomatic<IInternalUserFormValues>
                                id="phoneCell"
                                label="Cell phone"
                                useBds
                            >
                                <PhoneActionSheet
                                    value={values.phoneCell}
                                    testIdBase="cellPhoneActions"
                                    defaultCountryCode={
                                        internalUserDetails.internalUser.countryCode
                                    }
                                />
                            </BTFormItemAutomatic>
                            {values.smsOptInStatus !== SmsOptInStatus.None && (
                                <BTFormItemAutomatic<IInternalUserFormValues>
                                    id="smsOptInStatus"
                                    label="Receive text messages"
                                    useBds
                                >
                                    <CellPhoneOptInStatusTag
                                        smsOptInStatus={values.smsOptInStatus}
                                        useBds
                                    />
                                </BTFormItemAutomatic>
                            )}
                        </div>
                    )}
                </BdsSection>
            </BdsLayout>
            <BdsLayout
                displayType="1and1"
                id="roleSection"
                testid="roleSection"
                className="padding-top-xs"
            >
                <BdsSection>
                    <BTFormItemAutomatic<IInternalUserFormValues> id="role" useBds>
                        <RoleSelect
                            id="role"
                            treeData={roleTreeData}
                            onChange={onRoleChange}
                            onBlur={setFieldTouched}
                            readOnly={isReadOnly}
                            disabled={isReadOnly}
                            value={values.role}
                            data-testid="role"
                            useBds
                            addonAfter={
                                isFullAdmin && (
                                    <BdsButton
                                        to={manageRolesLink}
                                        useSPARouting
                                        target="_blank"
                                        testid="manage-roles"
                                        ariaLabel="Manage Roles"
                                        displayType="tertiary"
                                        icon={<BdsIconGearSix />}
                                    />
                                )
                            }
                        />
                    </BTFormItemAutomatic>
                    {canView && canEdit && (
                        <div className="flex flex-row">
                            <div className="flex flex-col">
                                {parseInt(selectedRole.id) !== -1 && (
                                    <div
                                        className="flex flex-row py-4"
                                        style={{ maxWidth: "var(--bui-token-spacing-160)" }}
                                    >
                                        <div className="flex flex-col">
                                            <BTLink
                                                to=""
                                                isUnderline={false}
                                                onClick={() =>
                                                    handleLinkClick(InternalUserTabKeys.Permissions)
                                                }
                                            >
                                                <BTCard size="small">
                                                    <div className="flex flex-row">
                                                        <div className="flex flex-col grow">
                                                            <BTTitle bold level={4}>
                                                                <div>
                                                                    {`${selectedRole.title} Permissions`}
                                                                </div>
                                                            </BTTitle>
                                                            {selectedRole?.extraData
                                                                ?.description && (
                                                                <ValueDisplay
                                                                    value={
                                                                        selectedRole.extraData
                                                                            .description
                                                                    }
                                                                />
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col RightArrowContainer">
                                                            <BTIconArrowRightOutlined size="large" />
                                                        </div>
                                                    </div>
                                                </BTCard>
                                            </BTLink>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </BdsSection>
                <div />
            </BdsLayout>
        </PageSection>
    );
}
